import React, { useState, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import truncate from 'lodash/truncate';
import isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';

import FixedImg from 'Components/Utility/FixedImg';
import useViewport from 'Hooks/useViewport';

import 'Components/Product/CriticReviews/ProductCriticReviewCard.scss';


const renderImage = (imageUrl, outletName) => {
  if (!imageUrl) return null;
  return imageUrl.includes('https://cdn.greenman.com/production/logos/') ? (
    <FixedImg width="46" src={imageUrl.replace('https://cdn.greenman.com/production/logos/', '')} alt={outletName} folder="logos" />
  ) : (
    <img src={imageUrl} alt={outletName} loading="lazy" />
  );
};

const StarScore = ({ starScore, outOfStarScore }) =>
  <div className="ProductCriticReviewCard__stars">
    {[...Array(outOfStarScore).keys()].map((score, index) => (
      <div className={classNames('ProductCriticReviewCard__star', { 'ProductCriticReviewCard__star--highlighted': (index + 1) <= starScore })} key={score}>
        <FontAwesomeIcon icon={faStar} />
      </div>
    ))}
  </div>;


const ProductCriticReviewCard = ({
  review, isCarouselCard, expandCards, setExpandCards, isVisible,
}) => {
  const cardRef = useRef();
  const overlayRef = useRef();
  const [showMoreOverlay, setShowMoreOverlay] = useState();
  const [showMore, setShowMore] = useState();
  const { width } = useViewport();

  const toggleExpandCard = (expand) => {
    const criticCard = _get(cardRef, 'current');
    if (!criticCard) return false;

    if (expand) {
      cardRef.current.style.setProperty('max-height', 'none', 'important');
    } else {
      cardRef.current.style.setProperty('max-height', '348px', 'important');
    }

    return setShowMore(expand);
  };

  useLayoutEffect(() => {
    setTimeout(() => {
      const isOverFlowing = cardRef && cardRef.current && cardRef.current.scrollHeight > 364;
      if (isOverFlowing) {
        setShowMoreOverlay(true);
      } else {
        setShowMoreOverlay(false);
      }
    }, 500);
  }, [review, isVisible, width]);


  if (isEmpty(review)) return null;


  const displayShowMoreBtn =
    (review.message && (review.message.length > 180) && ((review.message.length - 180) > 80));

  return (
    <figure ref={cardRef} className={classNames('ProductCriticReviewCard', { 'ProductCriticReviewCard--carouselCard': isCarouselCard })}>
      <div className="ProductCriticReviewCard__outlet">
        <figcaption className="ProductCriticReviewCard__outlet__name">
          <h3 className="h5">{review.outletName}</h3>
          {review.author &&
            <cite className="ProductCriticReviewCard__outlet__link">
              <a href={review.url} target="_blank" rel="noopener noreferrer nofollow">{review.author}</a>
            </cite>
          }
        </figcaption>
        <div className="ProductCriticReviewCard__outlet__image">
          {review.outletImage && renderImage(review.outletImage, review.outletName)}
        </div>
      </div>
      <div className="ProductCriticReviewCard__outlet__underline" />
      <blockquote cite={review.url}>
        {review.scoreType === 'stars' && <StarScore starScore={review.starScore} outOfStarScore={review.outOfStarScore} />}
        {review.scoreType === 'text' && <div className="ProductCriticReviewCard__scoreText">{review.displayScore}      {typeof review.displayScore === 'number' ? ' / 100' : ''}</div>}
        {isCarouselCard &&
          <div className="ProductCriticReviewCard__reviewText">
            {review.message}
          </div>
        }
        {!isCarouselCard &&
          <div className="ProductCriticReviewCard__reviewText">
            {!expandCards ? truncate(review.message, { length: 180, separator: ' ' }) : review.message}
            {!expandCards && displayShowMoreBtn &&
              <button className="ProductCriticReviewCard__showMoreButton" onClick={() => setExpandCards(!expandCards)}>
                <FormattedMessage id="product.critic.reviews.card.showmore" defaultMessage="Show more" />
              </button>
            }
          </div>
        }

      </blockquote>
      {isCarouselCard && showMoreOverlay &&
        <button
          ref={overlayRef}
          className={classNames('ProductCriticReviewCard__showMoreOverlayButton', { 'ProductCriticReviewCard__showMoreOverlayButton--noBackground': showMore })}
          onClick={() => toggleExpandCard(!showMore)}
        >
          {!showMore ?
            <React.Fragment>
              <FormattedMessage id="product.critic.reviews.card.showmore" defaultMessage="Show more" />
              <span>
                <FontAwesomeIcon icon={faChevronDown} className="ml-2" />
              </span>
            </React.Fragment>
            :
            <React.Fragment>
              <FormattedMessage id="product.critic.reviews.card.showless" defaultMessage="Show less" />
              <span>
                <FontAwesomeIcon icon={faChevronUp} className="ml-2" />
              </span>
            </React.Fragment>
          }
        </button>
      }
    </figure>
  );
};

ProductCriticReviewCard.propTypes = {
  review: PropTypes.shape({}),
  isCarouselCard: PropTypes.bool,
  expandCards: PropTypes.bool,
  setExpandCards: PropTypes.func.isRequired,
  isVisible: PropTypes.bool,
};

ProductCriticReviewCard.defaultProps = {
  review: null,
  isCarouselCard: false,
  expandCards: false,
  isVisible: false,
};

export default ProductCriticReviewCard;
